import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { useAuth, adminApi, formatApiError } from "./AuthContext";
import EventsPanel from "./EventsPanel";
import OrdersPanel from "./OrdersPanel";

const ROLES = ["admin", "coordinator", "check_in_staff"];

export default function AdminShell() {
  const { user, logout } = useAuth();

  if (user === null)
    return (
      <Centered>
        <span data-testid="admin-loading">Loading…</span>
      </Centered>
    );
  if (user === false) return <Navigate to="/admin/login" replace />;
  if (user.must_change_password)
    return <Navigate to="/admin/change-password" replace />;

  return (
    <div
      data-testid="admin-shell"
      style={{
        minHeight: "100vh",
        background: "#0A0F1F",
        color: "#fff",
        fontFamily: "system-ui, sans-serif",
        padding: "32px 24px",
      }}
    >
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          borderBottom: "1px solid #14213D",
          paddingBottom: 16,
          marginBottom: 28,
        }}
      >
        <div>
          <div style={{ fontSize: 12, opacity: 0.6, letterSpacing: 2 }}>
            THE NEXT STAGE — BACK OFFICE
          </div>
          <div style={{ marginTop: 6 }}>
            Signed in as{" "}
            <strong data-testid="admin-current-name">{user.name}</strong> —{" "}
            <span
              data-testid="admin-current-role"
              style={{
                padding: "2px 8px",
                background: "#E5B25D",
                color: "#0A0F1F",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 1,
              }}
            >
              {user.role.toUpperCase()}
            </span>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <a
            href="/admin/change-password"
            data-testid="admin-change-password-link"
            style={{
              padding: "8px 12px",
              background: "transparent",
              color: "#fff",
              border: "1px solid #2a3556",
              fontSize: 12,
              textDecoration: "none",
            }}
          >
            Change password
          </a>
          <button
            data-testid="admin-logout-btn"
            onClick={logout}
            style={btnSecondary}
          >
            Logout
          </button>
        </div>
      </header>

      <div style={{ marginBottom: 22 }}>
        <a
          href="/admin/checkin"
          data-testid="admin-open-checkin"
          style={{
            display: "inline-block",
            padding: "10px 16px",
            background: "#E5B25D",
            color: "#0A0F1F",
            fontWeight: 700,
            letterSpacing: 1,
            textDecoration: "none",
            fontSize: 13,
            marginRight: 10,
          }}
        >
          OPEN CHECK-IN SCANNER →
        </a>
        {(user.role === "admin" || user.role === "coordinator") && (
          <a
            href="/admin/console"
            data-testid="admin-open-console"
            style={{
              display: "inline-block",
              padding: "10px 16px",
              background: "#5dd6a4",
              color: "#0A0F1F",
              fontWeight: 700,
              letterSpacing: 1,
              textDecoration: "none",
              fontSize: 13,
            }}
          >
            OPEN SHOW CONSOLE →
          </a>
        )}
        {(user.role === "admin" || user.role === "coordinator") && (
          <a
            href="/admin/results"
            data-testid="admin-open-results"
            style={{
              display: "inline-block",
              padding: "10px 16px",
              background: "transparent",
              color: "#E5B25D",
              fontWeight: 700,
              letterSpacing: 1,
              textDecoration: "none",
              fontSize: 13,
              border: "1px solid #E5B25D",
              marginLeft: 10,
            }}
          >
            OPEN RESULTS →
          </a>
        )}
      </div>

      {user.role === "admin" ? (
        <>
          <AdminUsersPanel />
          <EventsPanel />
          <OrdersPanel />
        </>
      ) : user.role === "coordinator" ? (
        <>
          <EventsPanel />
          <OrdersPanel />
        </>
      ) : (
        <div
          data-testid="admin-noaccess-msg"
          style={{ opacity: 0.7, fontSize: 14 }}
        >
          You are signed in. Role-restricted features will appear here as they ship.
        </div>
      )}
    </div>
  );
}

function AdminUsersPanel() {
  const [items, setItems] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setErr("");
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/users");
      setItems(data.items || []);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <section data-testid="admin-users-panel">
      <h2 style={{ fontSize: 16, letterSpacing: 1, margin: "0 0 16px 0" }}>
        ADMIN USERS
      </h2>
      <CreateUserForm onCreated={load} />
      {err && (
        <div data-testid="admin-users-error" style={{ color: "#ff6b6b", margin: "12px 0" }}>
          {err}
        </div>
      )}
      {loading ? (
        <div>Loading users…</div>
      ) : (
        <table
          data-testid="admin-users-table"
          style={{ width: "100%", borderCollapse: "collapse", marginTop: 16 }}
        >
          <thead>
            <tr style={{ textAlign: "left", fontSize: 11, opacity: 0.6 }}>
              <th style={th}>NAME</th>
              <th style={th}>EMAIL</th>
              <th style={th}>ROLE</th>
              <th style={th}>STATUS</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {items.map((u) => (
              <UserRow key={u.id} u={u} reload={load} />
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function UserRow({ u, reload }) {
  const [busy, setBusy] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetMsg, setResetMsg] = useState("");
  const [resetErr, setResetErr] = useState("");
  const toggle = async () => {
    setBusy(true);
    try {
      await adminApi.patch(`/admin/users/${u.id}`, { is_active: !u.is_active });
      reload();
    } finally {
      setBusy(false);
    }
  };
  const resetPwd = async () => {
    setResetMsg("");
    setResetErr("");
    const temp = window.prompt(
      `Set a temporary password for ${u.email}. They will be forced to change it at next login.\n\n(min 10 characters)`
    );
    if (temp == null) return;
    if (temp.length < 10) {
      setResetErr("Temporary password must be at least 10 characters.");
      return;
    }
    setResetting(true);
    try {
      await adminApi.post(`/admin/users/${u.id}/reset-password`, {
        new_password: temp,
      });
      setResetMsg("Temp password set — share it with the user securely.");
      reload();
    } catch (e) {
      setResetErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setResetting(false);
    }
  };
  return (
    <tr data-testid={`admin-users-row-${u.email}`} style={{ borderTop: "1px solid #14213D" }}>
      <td style={td}>{u.name}</td>
      <td style={td}>
        {u.email}
        {u.must_change_password && (
          <span
            data-testid={`admin-users-must-change-${u.email}`}
            style={{
              marginLeft: 8,
              fontSize: 10,
              letterSpacing: 1,
              padding: "1px 6px",
              background: "rgba(229,178,93,0.18)",
              color: "#E5B25D",
              fontWeight: 700,
            }}
          >
            MUST CHANGE
          </span>
        )}
      </td>
      <td style={td}>{u.role}</td>
      <td style={td}>
        <span style={{ color: u.is_active ? "#5dd6a4" : "#ff6b6b" }}>
          {u.is_active ? "active" : "inactive"}
        </span>
      </td>
      <td style={{ ...td, textAlign: "right" }}>
        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
          <button
            data-testid={`admin-users-reset-${u.email}`}
            onClick={resetPwd}
            disabled={resetting}
            style={{
              ...btnSecondary,
              padding: "4px 10px",
              fontSize: 12,
              borderColor: "#E5B25D",
              color: "#E5B25D",
            }}
          >
            {resetting ? "Resetting…" : "Reset password"}
          </button>
          <button
            data-testid={`admin-users-toggle-${u.email}`}
            onClick={toggle}
            disabled={busy}
            style={{ ...btnSecondary, padding: "4px 10px", fontSize: 12 }}
          >
            {u.is_active ? "Deactivate" : "Reactivate"}
          </button>
        </div>
        {resetMsg && (
          <div
            data-testid={`admin-users-reset-msg-${u.email}`}
            style={{ marginTop: 6, fontSize: 11, color: "#5dd6a4" }}
          >
            {resetMsg}
          </div>
        )}
        {resetErr && (
          <div
            data-testid={`admin-users-reset-err-${u.email}`}
            style={{ marginTop: 6, fontSize: 11, color: "#ff6b6b" }}
          >
            {resetErr}
          </div>
        )}
      </td>
    </tr>
  );
}

function CreateUserForm({ onCreated }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("coordinator");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await adminApi.post("/admin/users", {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        role,
        password,
      });
      setName("");
      setEmail("");
      setPassword("");
      setRole("coordinator");
      onCreated();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="admin-create-user-form"
      style={{
        display: "grid",
        gridTemplateColumns: "1.2fr 1.5fr 1fr 1fr auto",
        gap: 10,
        alignItems: "end",
        padding: 14,
        background: "#101C35",
        border: "1px solid #14213D",
      }}
    >
      <Field label="Name">
        <input
          data-testid="admin-create-name"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={inputStyle}
        />
      </Field>
      <Field label="Email">
        <input
          data-testid="admin-create-email"
          required
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={inputStyle}
        />
      </Field>
      <Field label="Role">
        <select
          data-testid="admin-create-role"
          value={role}
          onChange={(e) => setRole(e.target.value)}
          style={inputStyle}
        >
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Initial Password">
        <input
          data-testid="admin-create-password"
          required
          minLength={8}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          style={inputStyle}
        />
      </Field>
      <button
        data-testid="admin-create-submit"
        type="submit"
        disabled={busy}
        style={{ ...btnPrimary, height: 36 }}
      >
        {busy ? "Adding…" : "Add User"}
      </button>
      {err && (
        <div data-testid="admin-create-error" style={{ gridColumn: "1 / -1", color: "#ff6b6b" }}>
          {err}
        </div>
      )}
    </form>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ fontSize: 11, letterSpacing: 1, opacity: 0.7 }}>
      {label.toUpperCase()}
      {children}
    </label>
  );
}

function Centered({ children }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        background: "#0A0F1F",
        color: "#fff",
        fontFamily: "system-ui, sans-serif",
      }}
    >
      {children}
    </div>
  );
}

const inputStyle = {
  width: "100%",
  padding: "8px 10px",
  background: "#0A0F1F",
  border: "1px solid #2a3556",
  color: "#fff",
  borderRadius: 2,
  marginTop: 4,
  fontSize: 13,
  boxSizing: "border-box",
};
const btnPrimary = {
  padding: "8px 14px",
  background: "#E5B25D",
  color: "#0A0F1F",
  border: 0,
  fontWeight: 700,
  cursor: "pointer",
};
const btnSecondary = {
  padding: "8px 14px",
  background: "transparent",
  color: "#fff",
  border: "1px solid #2a3556",
  cursor: "pointer",
};
const th = { padding: "10px 8px", fontWeight: 600 };
const td = { padding: "10px 8px", fontSize: 14 };
